import { useState, useEffect } from 'react';

interface PubSubTopic {
  name: string;
  messageCount: number;
  subscriptions: number;
  lastMessage: string | null;
  status: 'healthy' | 'warning' | 'error';
}

interface PubSubSubscription {
  name: string;
  topic: string;
  unackedMessages: number;
  deadLetterCount: number;
  ackDeadlineSeconds: number;
}

interface PubSubMessage {
  id: string;
  topic: string;
  data: unknown;
  attributes: Record<string, string>;
  publishTime: string;
  status: 'delivered' | 'pending' | 'dead-letter';
}

// Pub/Sub data - requires backend API connection
// TODO: Connect to /api/v1/debug/pubsub endpoint when available

export const PubSubMonitor = () => {
  const [topics, setTopics] = useState<PubSubTopic[]>([]);
  const [subscriptions, setSubscriptions] = useState<PubSubSubscription[]>([]);
  const [messages, setMessages] = useState<PubSubMessage[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'topics' | 'subscriptions' | 'messages'>('topics');

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      // TODO: Fetch from /api/v1/debug/pubsub when available
      // For now, show empty state - no fake data
      setTopics([]);
      setSubscriptions([]);
      setMessages([]);
      setIsLoading(false);
    };
    loadData();
  }, []);

  const totalMessages = topics.reduce((sum, t) => sum + t.messageCount, 0);
  const deadLetterCount = topics.find(t => t.name === 'dead-letter-queue')?.messageCount || 0;
  const healthyTopics = topics.filter(t => t.status === 'healthy').length;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return '🟢';
      case 'warning': return '🟡';
      case 'error': return '🔴';
      default: return '⚪';
    }
  };

  const getMessageStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'dead-letter': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const filteredMessages = selectedTopic 
    ? messages.filter(m => m.topic === selectedTopic)
    : messages;

  return (
    <div className="space-y-4">
      {/* Natural Language Summary */}
      <div className="bg-gradient-to-r from-indigo-900/50 to-violet-900/50 rounded-lg p-3 border border-indigo-500/30">
        <p className="text-sm">
          <span className="text-indigo-400 font-medium">Pub/Sub Status:</span>{' '}
          <span className="text-gray-200">
            {healthyTopics} of {topics.length} topics healthy, {totalMessages.toLocaleString()} messages today
            {deadLetterCount > 0 && (
              <>, <span className="text-red-400">{deadLetterCount} in dead letter queue</span></>
            )}
          </span>
        </p>
      </div>

      {/* View Mode Tabs */}
      <div className="flex gap-1 bg-gray-800 p-1 rounded-lg">
        {(['topics', 'subscriptions', 'messages'] as const).map((mode) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors ${
              viewMode === mode
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {mode === 'topics' && '📬 Topics'}
            {mode === 'subscriptions' && '📥 Subscriptions'}
            {mode === 'messages' && '📨 Messages'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500 text-xs">Loading Pub/Sub data...</div>
      ) : (
        <>
          {/* Topics View */}
          {viewMode === 'topics' && (
            <div className="bg-gray-800 rounded-lg p-3">
              <h4 className="text-sm font-medium text-indigo-400 mb-2">📬 Topics ({topics.length})</h4>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {topics.map((topic) => (
                  <div
                    key={topic.name}
                    onClick={() => {
                      setSelectedTopic(topic.name === selectedTopic ? null : topic.name);
                      setViewMode('messages');
                    }}
                    className={`flex items-center p-2 rounded cursor-pointer text-xs ${
                      selectedTopic === topic.name
                        ? 'bg-indigo-900/50 border border-indigo-500'
                        : 'bg-gray-900 hover:bg-gray-850'
                    }`}
                  >
                    <span className="w-6">{getStatusIcon(topic.status)}</span>
                    <span className="flex-1 text-gray-300 font-mono">{topic.name}</span>
                    <span className="w-20 text-right text-gray-500">{topic.messageCount.toLocaleString()} msgs</span>
                    <span className="w-16 text-right text-gray-600">{topic.lastMessage || 'never'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subscriptions View */}
          {viewMode === 'subscriptions' && (
            <div className="bg-gray-800 rounded-lg p-3">
              <h4 className="text-sm font-medium text-indigo-400 mb-2">📥 Subscriptions ({subscriptions.length})</h4>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {subscriptions.map((sub) => (
                  <div
                    key={sub.name}
                    className="flex items-center p-2 bg-gray-900 rounded text-xs"
                  >
                    <div className="flex-1">
                      <div className="text-gray-300 font-mono">{sub.name}</div>
                      <div className="text-[10px] text-gray-600">→ {sub.topic}</div>
                    </div>
                    <div className="text-right">
                      <div className={sub.unackedMessages > 0 ? 'text-yellow-400' : 'text-green-400'}>
                        {sub.unackedMessages} unacked
                      </div>
                      {sub.deadLetterCount > 0 && (
                        <div className="text-red-400 text-[10px]">{sub.deadLetterCount} dead</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Messages View */}
          {viewMode === 'messages' && (
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-indigo-400">
                  📨 Recent Messages
                  {selectedTopic && <span className="text-gray-500 ml-2">({selectedTopic})</span>}
                </h4>
                {selectedTopic && (
                  <button
                    onClick={() => setSelectedTopic(null)}
                    className="text-xs text-gray-500 hover:text-white"
                  >
                    Show All
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-2 bg-gray-900 rounded text-xs ${
                      msg.status === 'dead-letter' ? 'border border-red-500/50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-indigo-400 font-mono">{msg.topic}</span>
                      <span className={getMessageStatusColor(msg.status)}>{msg.status}</span>
                    </div>
                    <pre className="bg-gray-950 rounded p-1 text-[10px] text-gray-400 overflow-auto max-h-16">
                      {JSON.stringify(msg.data, null, 2)}
                    </pre>
                    <div className="flex items-center justify-between mt-1 text-[10px] text-gray-600">
                      <span>ID: {msg.id}</span>
                      <span>{new Date(msg.publishTime).toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Dead Letter Alert */}
      {deadLetterCount > 0 && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <span className="text-red-400">⚠️</span>
            <div>
              <div className="text-sm text-red-400 font-medium">Dead Letter Queue Alert</div>
              <div className="text-xs text-gray-400">
                {deadLetterCount} messages failed processing. Check worker logs for errors.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

